import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { Target, Plus, X, Power, Edit3, AlertTriangle, Wand2 } from "lucide-react";
import { toast } from "sonner";

// Phase 25.13af — Enterprise Watchlist manager. Lets super-admin curate
// the list of corporate networks (NAB, DBS, OpenAI, etc.) whose visits
// should be auto-flagged with a 🎯 WATCHLIST badge + +10 engagement boost.
//
// Phase 25.13ak — Now surfaces CEO / executive-transition context per
// entry. A bank with an interim CEO (e.g. BNK after Allan Savins' Jan-2026
// departure) shows an amber INTERIM badge + an on-row note so the operator
// reading the dashboard at 2am doesn't reflexively send a hard-pitch DM.
//
// Mounted below the HotSessionsPanel on /app/admin/visitors.

const CATEGORY_LABELS = {
  "au-bank": "AU Bank",
  "sg-bank": "SG Bank",
  "au-fintech": "AU Fintech",
  "sg-regulator": "SG Regulator",
  "sg-investor": "SG Investor",
  investor: "Investor",
  tech: "Tech",
  custom: "Custom",
};

// Transition status → display config. Empty / "stable" = no badge shown.
const TRANSITION_BADGES = {
  interim: { label: "INTERIM CEO", color: "text-[#FFB000] border-[#FFB000]/50 bg-[#FFB000]/10" },
  outgoing: { label: "CEO OUTGOING", color: "text-[#FF6B47] border-[#FF6B47]/50 bg-[#FF6B47]/10" },
  new_ceo: { label: "NEW CEO <90D", color: "text-[#00E676] border-[#00E676]/50 bg-[#00E676]/10" },
};

const TRANSITION_OPTIONS = [
  { value: "", label: "— stable / unknown —" },
  { value: "stable", label: "stable" },
  { value: "interim", label: "interim CEO (soft-touch)" },
  { value: "outgoing", label: "CEO outgoing" },
  { value: "new_ceo", label: "new CEO <90 days" },
];

export default function WatchlistManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("custom");
  const [newTokens, setNewTokens] = useState("");
  const [saving, setSaving] = useState(false);

  // Phase 25.13ak — meta edit state
  const [editEntry, setEditEntry] = useState(null);
  const [editNote, setEditNote] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editCurrentCeo, setEditCurrentCeo] = useState("");
  const [editOutgoingCeo, setEditOutgoingCeo] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // Phase 25.13al — dedupe state
  const [dedupeLoading, setDedupeLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/watchlist");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onAdd = async (e) => {
    e?.preventDefault();
    const tokens = newTokens.split(",").map(t => t.trim()).filter(Boolean);
    if (!newName.trim()) return toast.error("Name is required");
    if (tokens.length === 0) return toast.error("At least one match-token required");
    setSaving(true);
    try {
      await api.post("/admin/watchlist", {
        name: newName.trim(),
        category: newCategory,
        tokens,
      });
      setNewName(""); setNewTokens(""); setNewCategory("custom"); setShowAdd(false);
      toast.success(`Added ${newName.trim()} to watchlist`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (entry) => {
    try {
      await api.patch(`/admin/watchlist/${entry.id}`);
      toast.success(`${entry.name} ${entry.enabled ? "disabled" : "enabled"}`);
      await refresh();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const onDelete = async (entry) => {
    if (!confirm(`Remove "${entry.name}" from watchlist? Tokens will be lost.`)) return;
    try {
      await api.delete(`/admin/watchlist/${entry.id}`);
      toast.success(`Removed ${entry.name}`);
      await refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditNote(entry.note || "");
    setEditStatus(entry.transition_status || "");
    setEditCurrentCeo(entry.current_ceo || "");
    setEditOutgoingCeo(entry.outgoing_ceo || "");
  };

  const closeEdit = () => {
    setEditEntry(null);
    setEditNote(""); setEditStatus(""); setEditCurrentCeo(""); setEditOutgoingCeo("");
  };

  const onSaveMeta = async (e) => {
    e?.preventDefault();
    if (!editEntry) return;
    setSavingMeta(true);
    try {
      await api.patch(`/admin/watchlist/${editEntry.id}/meta`, {
        note: editNote,
        transition_status: editStatus,
        current_ceo: editCurrentCeo,
        outgoing_ceo: editOutgoingCeo,
      });
      toast.success(`Updated ${editEntry.name}`);
      closeEdit();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingMeta(false);
    }
  };

  const onDedupe = async () => {
    const confirmMsg = "Merge duplicate watchlist entries by name? Tokens + CEO metadata will be combined onto the longest-standing entry. Losers will be deleted. Safe to re-run.";
    if (!confirm(confirmMsg)) return;
    setDedupeLoading(true);
    try {
      const r = await api.post("/admin/watchlist/dedupe");
      const { groups_merged = 0, deleted_count = 0 } = r.data || {};
      if (deleted_count === 0) {
        toast.success("No duplicates found — watchlist is clean.");
      } else {
        toast.success(`Merged ${groups_merged} groups · removed ${deleted_count} duplicate entries`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dedupe failed");
    } finally {
      setDedupeLoading(false);
    }
  };

  const enabledCount = items.filter(i => i.enabled).length;
  const transitionCount = items.filter(
    i => i.enabled && i.transition_status && i.transition_status !== "stable",
  ).length;

  // Group by category for compact display
  const byCategory = items.reduce((acc, i) => {
    const c = i.category || "custom";
    (acc[c] ||= []).push(i);
    return acc;
  }, {});

  return (
    <Panel title="// ENTERPRISE WATCHLIST · AUTO-FLAG +10 ENGAGEMENT" testId="watchlist-manager">
      <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2 flex-wrap">
          <Target className="w-3 h-3 text-[#FF3B30]" />
          <span className="text-[#FF3B30] font-bold">{enabledCount} ACTIVE</span>
          <span className="text-zinc-700">·</span>
          <span className="text-zinc-600">{items.length} total</span>
          {transitionCount > 0 && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="inline-flex items-center gap-1 text-[#FFB000]" data-testid="watchlist-transition-count">
                <AlertTriangle className="w-3 h-3" />
                {transitionCount} IN TRANSITION
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onDedupe}
            disabled={dedupeLoading || items.length === 0}
            data-testid="watchlist-dedupe-btn"
            title="Merge duplicate entries by name. Combines tokens + CEO metadata onto the longest-standing entry."
            className="border border-zinc-700 hover:border-[#FFB000]/40 hover:bg-[#FFB000]/10 hover:text-[#FFB000] text-zinc-400 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40"
          >
            <Wand2 className="w-3 h-3" />
            {dedupeLoading ? "DEDUPING…" : "DEDUPE"}
          </button>
          <button
            type="button"
            onClick={() => setShowAdd(s => !s)}
            data-testid="watchlist-add-toggle"
            className="border border-[#FFB000]/40 hover:bg-[#FFB000]/10 text-[#FFB000] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" />
            {showAdd ? "CANCEL" : "ADD ENTRY"}
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={onAdd} className="px-4 py-4 border-b border-zinc-900 bg-zinc-950/40 space-y-3" data-testid="watchlist-add-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Name</label>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Bendigo Bank"
                data-testid="watchlist-input-name"
                className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:border-[#FFB000] focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Category</label>
              <select
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                data-testid="watchlist-input-category"
                className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:border-[#FFB000] focus:outline-none"
              >
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Match tokens (comma-separated)</label>
              <input
                type="text"
                value={newTokens}
                onChange={e => setNewTokens(e.target.value)}
                placeholder="bendigo and adelaide bank, bendigo bank"
                data-testid="watchlist-input-tokens"
                className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:border-[#FFB000] focus:outline-none"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mr-auto">
              Tokens match against `asn_org` (case-insensitive substring). Add CEO context via the ✎ pencil after creating.
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="watchlist-add-submit"
              className="border border-[#00E676]/40 hover:bg-[#00E676]/10 text-[#00E676] px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? "ADDING…" : "ADD TO WATCHLIST"}
            </button>
          </div>
        </form>
      )}

      <div className="p-4">
        {loading ? (
          <div className="font-mono text-[11px] text-zinc-600 py-4 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="font-mono text-[11px] text-zinc-600 py-4 text-center">No entries — defaults seed on first dashboard load.</div>
        ) : (
          <div className="space-y-3">
            {Object.entries(byCategory).map(([cat, entries]) => (
              <div key={cat}>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-1.5">
                  {CATEGORY_LABELS[cat] || cat.toUpperCase()} · {entries.length}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {entries.map(e => {
                    const badge = TRANSITION_BADGES[e.transition_status];
                    const tip = [
                      `Tokens: ${(e.tokens || []).join(" · ")}`,
                      e.current_ceo ? `Current CEO: ${e.current_ceo}` : null,
                      e.outgoing_ceo ? `Outgoing: ${e.outgoing_ceo}` : null,
                      e.note ? `Note: ${e.note}` : null,
                    ].filter(Boolean).join("\n");
                    return (
                      <div
                        key={e.id}
                        className={`group inline-flex items-center gap-1.5 border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                          e.enabled
                            ? "border-[#FF3B30]/40 bg-[#FF3B30]/5 text-[#FF6B5C]"
                            : "border-zinc-800 bg-zinc-950/40 text-zinc-600 line-through"
                        }`}
                        data-testid={`watchlist-entry-${e.id}`}
                        title={tip}
                      >
                        <Target className="w-2.5 h-2.5 flex-shrink-0" />
                        <span>{e.name}</span>
                        {badge && (
                          <span
                            className={`border px-1 py-0.5 text-[8px] tracking-widest ${badge.color}`}
                            data-testid={`watchlist-transition-${e.id}`}
                          >
                            {badge.label}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => openEdit(e)}
                          className="ml-1 hover:text-[#FFB000]"
                          aria-label={`Edit CEO context for ${e.name}`}
                          title="Edit CEO / transition context"
                          data-testid={`watchlist-edit-meta-${e.id}`}
                        >
                          <Edit3 className="w-2.5 h-2.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggle(e)}
                          className="hover:text-[#FFB000]"
                          aria-label={`${e.enabled ? "Disable" : "Enable"} ${e.name}`}
                          title={`${e.enabled ? "Disable" : "Enable"}`}
                        >
                          <Power className="w-2.5 h-2.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(e)}
                          className="hover:text-[#FF3B30]"
                          aria-label={`Delete ${e.name}`}
                          title="Delete"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase 25.13ak — meta edit modal (CEO / transition context) */}
      {editEntry && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}
          data-testid="watchlist-meta-modal"
        >
          <div className="w-full max-w-xl border border-[#FFB000]/40 bg-black">
            <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FFB000]">
                EDIT CEO / TRANSITION · {editEntry.name}
              </div>
              <button
                type="button"
                onClick={closeEdit}
                className="text-zinc-500 hover:text-white"
                aria-label="Close"
                data-testid="watchlist-meta-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={onSaveMeta} className="px-4 py-4 space-y-3">
              <div>
                <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Transition status</label>
                <select
                  value={editStatus}
                  onChange={e => setEditStatus(e.target.value)}
                  className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:border-[#FFB000] focus:outline-none"
                  data-testid="watchlist-meta-status"
                >
                  {TRANSITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Current CEO</label>
                  <input
                    type="text"
                    value={editCurrentCeo}
                    onChange={e => setEditCurrentCeo(e.target.value)}
                    placeholder="Steve Kinsella (Interim, ex-CFO)"
                    className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:border-[#FFB000] focus:outline-none"
                    data-testid="watchlist-meta-current-ceo"
                  />
                </div>
                <div>
                  <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Outgoing CEO</label>
                  <input
                    type="text"
                    value={editOutgoingCeo}
                    onChange={e => setEditOutgoingCeo(e.target.value)}
                    placeholder="Allan Savins (departed Jan 2026)"
                    className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:border-[#FFB000] focus:outline-none"
                    data-testid="watchlist-meta-outgoing-ceo"
                  />
                </div>
              </div>
              <div>
                <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Outreach note (shown on tooltip + HOT-session row)</label>
                <textarea
                  value={editNote}
                  onChange={e => setEditNote(e.target.value)}
                  rows={3}
                  maxLength={400}
                  placeholder="Soft-touch only. Approach NED Martin Barrett or Better Choice GM. Permanent CEO search ~3-6 months."
                  className="w-full mt-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:border-[#FFB000] focus:outline-none"
                  data-testid="watchlist-meta-note"
                />
                <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-700 mt-1">{editNote.length}/400</div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="border border-zinc-800 hover:bg-zinc-900 text-zinc-400 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest"
                  data-testid="watchlist-meta-cancel"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  disabled={savingMeta}
                  className="border border-[#00E676]/40 hover:bg-[#00E676]/10 text-[#00E676] px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
                  data-testid="watchlist-meta-save"
                >
                  {savingMeta ? "SAVING…" : "SAVE"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Panel>
  );
}
